import React from 'react';
import {
  Alert,
  Box,
  Typography,
  LinearProgress,
  Fade,
  IconButton
} from '@mui/material';
import {
  Close,
  CloudSync,
  CheckCircle,
  Error
} from '@mui/icons-material';

/**
 * ImportNotification component for showing import status in top-right corner
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the notification
 * @param {string} props.type - Type of import (e.g., 'Full Import', 'Delta Import')
 * @param {string} props.status - Status of import ('running', 'success', 'error')
 * @param {string} props.message - Message to display
 * @param {Function} props.onClose - Callback when notification is closed
 * @returns {JSX.Element} ImportNotification component
 */
const ImportNotification = ({ show, type, status, message, onClose }) => {
  if (!show) return null;

  const getIcon = () => {
    switch (status) {
      case 'running':
        return <CloudSync />;
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <Error />;
      default:
        return <CloudSync />;
    }
  };

  const getSeverity = () => {
    switch (status) {
      case 'running':
        return 'info';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'running':
        return `${type} Running...`;
      case 'success':
        return `${type} Completed`;
      case 'error':
        return `${type} Failed`;
      default:
        return type;
    }
  };

  return (
    <Fade in={show}>
      <Box
        sx={{
          position: 'fixed',
          top: 80, // Below the header
          right: 16,
          zIndex: 1300,
          minWidth: 300,
          maxWidth: 400
        }}
      >
        <Alert
          severity={getSeverity()}
          icon={getIcon()}
          action={
            status !== 'running' && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={onClose}
              >
                <Close fontSize="inherit" />
              </IconButton>
            )
          }
          sx={{
            boxShadow: 3,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="subtitle2" component="div" gutterBottom>
            {getTitle()}
          </Typography>
          {message && (
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          )}
          {status === 'running' && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress size="small" />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Please don't close this page while import is running...
              </Typography>
            </Box>
          )}
        </Alert>
      </Box>
    </Fade>
  );
};

export default ImportNotification;
